import '../css/app.css';
import 'leaflet/dist/leaflet.css';
import { createRoot } from 'react-dom/client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import Sidebar from './components/Sidebar';
import MobilniMenu from './components/sidebar/MobilniMenu';
import { findPageMeta, modulStranky, muze, smiNaStranku, vidiModul, viditelneSekce } from './lib/moduly';
import { oznacIosInstalaci } from './lib/iosInstalace';
import naPlose from './lib/naPlose';
import { jeAplikace, smiVAplikaci, reverbKonfigurace } from './lib/aplikace';
import BezPristupu from './components/BezPristupu';
import NovaVerze from './components/layout/NovaVerze';
import OtocNaVysku from './components/layout/OtocNaVysku';
import LoginScreen from './pages/LoginScreen';
import Dashboard from './pages/Dashboard';
import Zamestnanci from './pages/Zamestnanci';
import Smeny from './pages/smeny/SmenyModul';
import Prehled from './pages/Prehled';
import OnboardingModal from './pages/OnboardingModal';
import MojeSkoleniDetail from './pages/MojeSkoleniDetail';
import ReportSmenyStranka from './pages/pokladna/ReportSmenyStranka';
import Aktivita from './pages/Aktivita';
import Spizirna from './pages/Spizirna';
import Balirna from './pages/Balirna';
import Objednavky from './pages/Objednavky';
import Zakaznici from './pages/Zakaznici';
import Checklisty from './pages/Checklisty';

import Kamery from './pages/Kamery';
import Udrzba from './pages/Udrzba';
import Rozvoz from './pages/Rozvoz';
import Sprava from './pages/Sprava';
import FlotilaErrorBoundary from './components/FlotilaErrorBoundary';
import Podnety from './pages/Podnety';
import Finance from './pages/Finance';
import DokladyMobilUpload from './pages/DokladyMobilUpload';
import Zpravy from './pages/Zpravy';
import Znalosti from './pages/Znalosti';
import ZnalostiSheet from './components/znalosti/ZnalostiSheet';
import ZnalostiStranka from './pages/ZnalostiStranka';
import Media from './pages/Media';
import Social from './pages/Social';
import Web from './pages/Web';
import KdsTestConsole from './pages/KdsTestConsole';
import RozvozCockpit from './pages/RozvozCockpit';
import MiniChat from './components/MiniChat';
import InAppToast from './components/InAppToast';
import TestOdznak from './components/TestOdznak';
import { cilNotifikace, jeAkcniNotifikace, oznacitPrectenou } from './lib/notifikace';
import PodnetWidget from './components/PodnetWidget';
import UploadProgressPopup from './components/UploadProgressPopup';
import PwaInstallBanner from './components/PwaInstallBanner';
import LaditRozvrzeni from './components/LaditRozvrzeni';
import MobilniLista, { REZERVA_LISTY } from './components/MobilniLista';
import { SekceContext } from './contexts/SekceContext';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// Klik na toast notifikace vede rovnou na věc a notifikaci označí přečtenou.
// Akční notifikace (prohození směny, žádost o nepřítomnost — tlačítka jsou
// přímo v seznamu) a notifikace bez cíle otevřou seznam notifikací.
function toastCilNotifikace(n, user) {
    const cil = !jeAkcniNotifikace(n, user) && cilNotifikace(n, user);
    return cil ? { page: cil.page, params: cil.params, notifikaceId: n.id } : { page: 'aktivita' };
}

async function sendSubscriptionToServer(subscription) {
    const keys = subscription.toJSON().keys;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
        body: JSON.stringify({
            endpoint: subscription.endpoint,
            p256dh: keys.p256dh,
            auth: keys.auth,
        }),
    });
}

// ── Adresa jako stav navigace ──────────────────────────────────────
// Otevřenou stránku držel jen `useState`, takže adresa zůstávala pořád `/`.
// Zpět (tlačítko i gesto) tím pádem odešlo z celé aplikace — v appce na ploše
// ji rovnou zavřelo — obnovení stránky spadlo na Přehled a odkaz na konkrétní
// místo nešlo poslat. Stav proto bydlí v hashi: `#/stranka?klic=hodnota`.
//
// Hash, ne cesta: Laravel servíruje SPA na `/` a o dalších cestách neví, takže
// `/finance` by po obnovení skončilo na 404. Hash server nikdy neuvidí.
//
// Do adresy jdou jen jednoduché hodnoty. Parametry, které nesou objekt
// (`polozka` z prokliku na surovinu), zůstávají v paměti a obnovení stránky je
// zahodí — stránka pak otevře svůj výchozí stav, což všechny umí.
function cestaZAdresy() {
    const hash = window.location.hash.replace(/^#\/?/, '');
    if (!hash) return null;
    const [cesta, dotaz] = hash.split('?');
    if (!cesta) return null;
    const params = {};
    new URLSearchParams(dotaz || '').forEach((hodnota, klic) => {
        // Čísla se musí vrátit jako čísla — `konverzaceId` a spol. se jinde
        // porovnávají přes `===` a z adresy by přišel řetězec.
        params[klic] = /^-?\d+$/.test(hodnota) ? Number(hodnota) : hodnota;
    });
    return { page: decodeURIComponent(cesta), params };
}

function adresaZeStavu(page, params) {
    const jednoduche = Object.entries(params || {})
        .filter(([, v]) => v != null && ['string', 'number', 'boolean'].includes(typeof v))
        .map(([k, v]) => [k, String(v)]);
    const dotaz = new URLSearchParams(jednoduche).toString();
    return `#/${encodeURIComponent(page)}${dotaz ? `?${dotaz}` : ''}`;
}

// Stránky zrušených modulů → modul a záložka, kam se přestěhovaly.
const PRESMEROVANE_STRANKY = {
    // Vzdělávání a Hodnocení → Lidé (2026-09-28)
    skoleni: ['zamestnanci', 'skoleni'],
    // Editor školení je detail v záložce Školení — `skoleniId` jde s sebou
    'skoleni-editor': ['zamestnanci', 'skoleni'],
    hodnoceni: ['zamestnanci', 'hodnoceni'],
    // Rezervace → Hosté, dnes Objednávky (2026-10-06) — `id` jde s sebou
    rezervace: ['objednavky', 'rezervace'],
    // Zákazníci: 2026-10-06 záložka Hostů, od 2026-10-12 vlastní modul
    'zakaznicky-zazitek': ['zakaznici', 'seznam'],
    // Flotila → Rozvoz (2026-10-10). Záložka podle staré: tankování, servis
    // a pojištění jsou teď v detailu auta. Karta řidiče je detail v Řidičích.
    flotila: ['rozvoz', null, { live: 'live', ridici: 'ridici', tankovani: 'auta', servis: 'auta', pojisteni: 'auta' }],
    'flotila-karta-ridice': ['rozvoz', 'ridici'],
};

function App() {
    const { theme } = useTheme();
    const isDarkApp = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [user, setUser] = useState(null);
    // Osobní nastavení v telefonu (navigace rozvozce) se klíčuje podle
    // uživatele — firemní telefony si lidé předávají.
    useEffect(() => { window.__flUzivatelId = user?.id ?? null; }, [user?.id]);
    const handleUserUpdate = (updated) => setUser(updated);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [selectedSkoleniId, setSelectedSkoleniId] = useState(null);
    const [selectedKonverzaceId, setSelectedKonverzaceId] = useState(null);
    const [currentParams, setCurrentParams] = useState({});


    const [quickBadges, setQuickBadges] = useState({ zpravy: 0, notifikace: 0 });
    // Odznak na ikoně se nastavuje až podle načtených počtů — výchozí nuly by ho
    // při startu aplikace na okamžik smazaly.
    const [odznakyNacteny, setOdznakyNacteny] = useState(false);
    // Sekce stránky pro mobilní lištu (useSekce) — pole i obsluha výběru.
    const [sekceListy, setSekceListy] = useState(null);
    // Počty u modulů (nepodepsané směrnice, nedokončená školení) — mobilní menu
    // a součtový odznak na profilovce
    const [showPushBanner, setShowPushBanner] = useState(false);

    // ── Upload progress (globální — přežije navigaci mezi moduly) ──
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadPopupMin, setUploadPopupMin] = useState(false);

    const handleMediaUpload = async (files, slozka) => {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        const initFiles = files.map(f => ({ name: f.name, size: f.size, progress: 0, status: 'waiting', error: null }));
        setUploadFiles(initFiles);
        setUploadPopupMin(false);

        const uploadOne = (file, index) => new Promise((resolve) => {
            const fd = new FormData();
            fd.append('soubory[]', file);
            if (slozka) fd.append('slozka', slozka);

            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const pct = Math.round((e.loaded / e.total) * 100);
                    setUploadFiles(prev => prev.map((f, i) => i === index ? { ...f, progress: pct, status: 'uploading' } : f));
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    setUploadFiles(prev => prev.map((f, i) => i === index ? { ...f, progress: 100, status: 'done' } : f));
                    resolve(true);
                } else {
                    let errMsg = 'Chyba při nahrávání';
                    try { const err = JSON.parse(xhr.responseText); errMsg = err.error || err.message || errMsg; } catch {}
                    setUploadFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'error', error: errMsg } : f));
                    resolve(false);
                }
            });

            xhr.addEventListener('error', () => {
                setUploadFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'error', error: 'Síťová chyba' } : f));
                resolve(false);
            });

            xhr.open('POST', '/api/media');
            xhr.setRequestHeader('X-CSRF-TOKEN', csrfToken);
            setUploadFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'uploading' } : f));
            xhr.send(fd);
        });

        for (let i = 0; i < files.length; i++) {
            await uploadOne(files[i], i);
        }
    };

    // Mobile detection — aplikace je mobilní vždy, i na tabletu
    const [isMobile, setIsMobile] = useState(() => jeAplikace() || window.innerWidth < 768);
    // Celoobrazovkové mobilní menu (MobilniMenu) — otevírá ho profilovka v hlavičce.
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handler = () => {
            const mobile = jeAplikace() || window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setMobileMenuOpen(false);
        };
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const chatParam = params.get('chat');
        const pageParam = params.get('page');
        const notifikaceParam = params.get('notifikace');
        // Adresa zapsaná při minulé návštěvě (obnovení stránky, záložka,
        // sdílený odkaz) má přednost před výchozí stránkou.
        const zAdresy = cestaZAdresy();

        fetch('/auth/me')
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                setUser(data);
                if (data?.zamestnanec_id) {
                    let cilovaStranka = 'prehled';
                    let ciloveParams = {};
                    if (zAdresy) {
                        cilovaStranka = zAdresy.page;
                        ciloveParams = zAdresy.params;
                        if (ciloveParams.skoleniId !== undefined) setSelectedSkoleniId(ciloveParams.skoleniId);
                        if (ciloveParams.konverzaceId !== undefined) setSelectedKonverzaceId(ciloveParams.konverzaceId);
                    } else if (chatParam) {
                        cilovaStranka = 'zpravy';
                        ciloveParams = { konverzaceId: parseInt(chatParam) };
                        setSelectedKonverzaceId(parseInt(chatParam));
                    } else if (pageParam) {
                        // Staré odkazy a QR kódy (`/?page=doklady-mobil-upload`).
                        // Whitelist tu býval „pro bezpečnost", jenže tu drží
                        // `smiNaStranku` u každého renderu — a s adresou by
                        // whitelist znamenal, že odkaz vede jen na čtyři místa.
                        cilovaStranka = pageParam;
                    }
                    setCurrentPage(cilovaStranka);
                    setCurrentParams(ciloveParams);
                    // Adresa ať od začátku odpovídá tomu, co je na obrazovce,
                    // ale bez nového záznamu — Zpět z první stránky má vést ven.
                    // Cesta se píše celá, ať z adresy zmizí i stará query
                    // (?page=, ?notifikace=) — jinak by se při obnovení spustila znovu.
                    window.history.replaceState({}, '', '/' + adresaZeStavu(cilovaStranka, ciloveParams));
                    if (data?.zamestnanec_stav === 'novy') {
                        setShowOnboarding(true);
                    }
                } else if (chatParam || pageParam || notifikaceParam) {
                    window.history.replaceState({}, '', '/');
                }
                // Klik na push notifikaci při zavřené aplikaci (`navigate` / sw.js) → rovnou na věc
                if (data && notifikaceParam) {
                    fetch(`/api/notifikace/${parseInt(notifikaceParam)}`)
                        .then(r => r.ok ? r.json() : null)
                        .then(n => {
                            if (!n) return;
                            const cil = toastCilNotifikace(n, data);
                            if (cil.notifikaceId) oznacitPrectenou(cil.notifikaceId);
                            navigateTo(cil.page, cil.params);
                        })
                        .catch(() => {});
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Laravel Echo — WebSocket (Reverb) + user kanál pro badge
    useEffect(() => {
        if (!user) return;
        if (!window.Echo) {
            window.Pusher = Pusher;
            // Web má hodnoty z buildu, aplikace ze serveru (lib/aplikace.js)
            const reverb = reverbKonfigurace();
            window.Echo = new Echo({
                broadcaster: 'reverb',
                key: reverb.key,
                wsHost: reverb.host,
                wsPort: reverb.port ?? 8080,
                wssPort: reverb.port ?? 443,
                forceTLS: reverb.scheme === 'https',
                enabledTransports: ['ws', 'wss'],
                authEndpoint: '/broadcasting/auth',
            });
        }
        // User-level kanál: badge update při nové zprávě v libovolné konverzaci
        // Stránky, které se otevřely dřív než spojení (Objednávky → Živě…),
        // se podle tohohle připojí ke svým kanálům až teď.
        window.dispatchEvent(new Event('echo-pripraveno'));
        const ch = window.Echo.private(`user.${user.id}`);
        ch.listen('.NovaChatZprava', (e) => {
            const zprava = e.zprava;
            // Optimistický inkrement — okamžitý badge bez HTTP round-tripu.
            // Ne u otevřené konverzace: tu chat rovnou označí jako přečtenou
            // a odznak by jinak vyskočil a zase spadl.
            const otevrena = String(window.__aktivniKonverzaceId) === String(zprava?.konverzace_id)
                && document.visibilityState === 'visible';
            if (!otevrena && zprava?.odesilatel_id !== user?.id) {
                // Aktualizovat refs, ať pojistný polling neukáže stejnou zprávu
                // podruhé (druhý toast bez fotky / s jinou fotkou)
                prevBadgesRef.current = { ...prevBadgesRef.current, zpravy: (prevBadgesRef.current.zpravy || 0) + 1 };
                setQuickBadges(prev => {
                    const next = { ...prev, zpravy: (prev.zpravy || 0) + 1 };
                    window.dispatchEvent(new CustomEvent('chat-badge-update', { detail: { count: next.zpravy } }));
                    return next;
                });
            }
            // In-app toast — pokud NENÍ otevřená tato konverzace
            if (zprava?.odesilatel_id !== user?.id) {
                if (String(window.__aktivniKonverzaceId) !== String(zprava?.konverzace_id)) {
                    window.dispatchEvent(new CustomEvent('in-app-toast', { detail: {
                        id: `chat-${zprava.id}`,
                        title: zprava.odesilatel?.jmeno || 'Zpráva',
                        body: zprava.obsah || '📎 Soubor',
                        avatar: zprava.odesilatel?.avatar_url,
                        onClick: { page: 'zpravy', params: { konverzaceId: zprava.konverzace_id } },
                    }}));
                }
            }
        });
        // Realtime broadcast pro systémové notifikace — odpaluje
        // [[StgNotifikace]] booted() při každém StgNotifikace::create.
        // Polling v dalším useEffectu slouží už jen jako fallback.
        ch.listen('.NovaNotifikace', (e) => {
            const n = e.notifikace;
            setQuickBadges(prev => {
                const next = { ...prev, notifikace: (prev.notifikace || 0) + 1 };
                return next;
            });
            // Aktualizovat refs aby polling neukázal toast podruhé
            prevBadgesRef.current = {
                ...prevBadgesRef.current,
                notifikace: (prevBadgesRef.current.notifikace || 0) + 1,
            };
            window.dispatchEvent(new Event('notifikace-changed'));
            window.dispatchEvent(new Event('notifikace-seznam'));
            // In-app toast — InAppToast filtruje duplicity přes localStorage SEEN_KEY,
            // takže notif-{id} se stejně objeví jen jednou (i mezi reloady).
            window.dispatchEvent(new CustomEvent('in-app-toast', { detail: {
                id: `notif-${n.id}`,
                title: n.titulek || 'Notifikace',
                body: n.zprava || '',
                icon: 'bell',
                // Modul dává toastu barvu a ikonu (viz `vzhledModulu`) — bez něj
                // vypadala notifikace Směn stejně jako zpráva od kolegy.
                modul: n.modul,
                onClick: toastCilNotifikace(n, user),
            }}));
        });
        // Notifikace se změnily na serveru (akční notifikaci vyřídil někdo jiný) —
        // přepočítat odznak a obnovit otevřený seznam, ať zmizí tlačítka
        ch.listen('.NotifikaceZmenena', () => {
            window.dispatchEvent(new Event('notifikace-changed'));
            window.dispatchEvent(new Event('notifikace-seznam'));
        });
        return () => window.Echo?.leave(`user.${user.id}`);
    }, [user]);

    // Doklady live — někdo nahrál doklad (mobil, e-mailová podatelna, jiný stroj).
    // Toast vyskočí kdekoli v OS, ne jen nad otevřeným modulem Finance; klik
    // otevře rovnou ten doklad. Vlastní upload si netoastujeme.
    // Pozn.: kanál sdílíme s FinanceDokladyInbox — proto stopListening, ne leave.
    useEffect(() => {
        if (!user || !['superadmin', 'admin'].includes(user.role) || !window.Echo) return;
        let kanal;
        try {
            kanal = window.Echo.private('doklady.live');
        } catch (e) {
            return;
        }
        const naNovyDoklad = (e) => {
            const d = e?.doklad;
            if (!d) return;
            if (e?.nahral?.id && e.nahral.id === user.id) return;
            const kdo = e?.nahral?.jmeno ? `${e.nahral.jmeno} · ` : '';
            const co = e?.nazev_souboru || d.interni_cislo || 'čeká na AI scan';
            window.dispatchEvent(new CustomEvent('in-app-toast', { detail: {
                id: `doklad-${d.id}`,
                title: 'Nový doklad',
                body: `${kdo}${co}`,
                icon: 'bell',
                modul: 'finance',
                onClick: { page: 'finance', params: { tab: 'doklady', dokladId: d.id } },
            }}));
        };
        kanal.listen('.NovyDoklad', naNovyDoklad);
        return () => kanal.stopListening('.NovyDoklad', naNovyDoklad);
    }, [user]);

    // Badge counts pro mobilní quick menu + desktop header
    const prevBadgesRef = useRef({ zpravy: 0, notifikace: 0 });
    useEffect(() => {
        if (!user) return;
        const fetchBadges = () => {
            Promise.all([
                fetch('/api/chat/neprectenych').then(r => r.ok ? r.json() : { count: 0 }),
                fetch('/api/notifikace/pocet').then(r => r.ok ? r.json() : { pocet: 0 }),
            ]).then(([chat, notif]) => {
                const zpravyCount = chat.count ?? 0;
                const notifCount = notif.pocet ?? 0;
                // Odznak Notifikací = nepřečtené + úkoly, které dnes/po termínu hoří
                const horiUkolu = notif.ukoly ?? 0;
                const prev = prevBadgesRef.current;

                // Nové chat zprávy — zobrazit toast (fallback bez WebSocketu)
                if (zpravyCount > prev.zpravy && prev.zpravy >= 0) {
                    fetch('/api/chat/konverzace').then(r => r.ok ? r.json() : []).then(konverzace => {
                        const neprecte = konverzace.filter(k => k.neprectenych > 0 && String(window.__aktivniKonverzaceId) !== String(k.id));
                        if (neprecte.length > 0) {
                            const k = neprecte[0];
                            const druhy = k.typ === 'soukromy' ? k.ucastnici?.find(u => u.id !== user.id) : null;
                            window.dispatchEvent(new CustomEvent('in-app-toast', { detail: {
                                // Stejné id jako realtime toast — InAppToast duplicitu zahodí
                                id: k.posledni_zprava?.id ? `chat-${k.posledni_zprava.id}` : `chat-poll-${k.id}-${Date.now()}`,
                                title: k.typ === 'soukromy' ? (druhy?.jmeno || k.nazev) : (k.nazev || 'Zpráva'),
                                body: k.posledni_zprava?.obsah || 'Nová zpráva',
                                avatar: druhy?.avatar_url || null,
                                onClick: { page: 'zpravy', params: { konverzaceId: k.id } },
                            }}));
                        }
                    }).catch(() => {});
                }

                // Nové systémové notifikace — zobrazit toast
                if (notifCount > prev.notifikace && prev.notifikace >= 0) {
                    fetch('/api/notifikace?limit=1').then(r => r.ok ? r.json() : []).then(data => {
                        const notifList = Array.isArray(data) ? data : (data.data || []);
                        if (notifList.length > 0) {
                            const n = notifList[0];
                            window.dispatchEvent(new CustomEvent('in-app-toast', { detail: {
                                id: `notif-${n.id}`,
                                title: n.titulek || 'Notifikace',
                                body: n.text || n.zprava || '',
                                icon: 'bell',
                                onClick: toastCilNotifikace(n, user),
                            }}));
                        }
                    }).catch(() => {});
                }

                prevBadgesRef.current = { zpravy: zpravyCount, notifikace: notifCount };
                setQuickBadges({ zpravy: zpravyCount, notifikace: notifCount + horiUkolu });
                setOdznakyNacteny(true);
                window.dispatchEvent(new CustomEvent('chat-badge-update', { detail: { count: zpravyCount } }));
                window.dispatchEvent(new CustomEvent('notifikace-changed', { detail: { zdroj: 'app' } }));
            }).catch(() => {});
        };
        fetchBadges();
        // Při připojeném WebSocketu chodí změny realtime — polling je jen pojistka
        // jednou za minutu; bez něj každých 10 s
        let posledniDotaz = Date.now();
        const i = setInterval(() => {
            const zive = window.Echo?.connector?.pusher?.connection?.state === 'connected';
            if (zive && Date.now() - posledniDotaz < 60_000) return;
            posledniDotaz = Date.now();
            fetchBadges();
        }, 10_000);
        // Push přišel, zatímco je aplikace otevřená
        const naPush = (e) => { if (e.data?.type === 'PUSH_RECEIVED') fetchBadges(); };
        navigator.serviceWorker?.addEventListener('message', naPush);
        // Při přečtení zpráv — re-fetch přesného počtu
        window.addEventListener('chat-nova-zprava', fetchBadges);
        // Úkol odkliknutý / převzatý — odznak hned
        window.addEventListener('ukoly-zmena', fetchBadges);
        // Přečtení jinde (Zprávy, Aktivita) — odznaky hned, ne až po dalším pollingu
        const naChatBadge = (e) => {
            const count = e.detail?.count;
            if (typeof count === 'number') setQuickBadges(prev => (prev.zpravy === count ? prev : { ...prev, zpravy: count }));
        };
        const naNotifikace = (e) => {
            if (e.detail?.zdroj === 'app') return;
            fetch('/api/notifikace/pocet').then(r => r.ok ? r.json() : null).then(d => {
                if (!d) return;
                prevBadgesRef.current = { ...prevBadgesRef.current, notifikace: d.pocet ?? 0 };
                setQuickBadges(prev => ({ ...prev, notifikace: (d.pocet ?? 0) + (d.ukoly ?? 0) }));
            }).catch(() => {});
        };
        window.addEventListener('chat-badge-update', naChatBadge);
        window.addEventListener('notifikace-changed', naNotifikace);
        return () => {
            clearInterval(i);
            navigator.serviceWorker?.removeEventListener('message', naPush);
            window.removeEventListener('chat-nova-zprava', fetchBadges);
            window.removeEventListener('ukoly-zmena', fetchBadges);
            window.removeEventListener('chat-badge-update', naChatBadge);
            window.removeEventListener('notifikace-changed', naNotifikace);
        };
    }, [user]);

    // Heartbeat — online přítomnost
    useEffect(() => {
        if (!user) return;
        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        const ping = () => fetch('/auth/heartbeat', { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf } });
        ping();
        const i = setInterval(ping, 60_000);
        return () => clearInterval(i);
    }, [user]);

    // Push notification subscription
    useEffect(() => {
        if (!user) return;
        // Aplikace dostává push nativně (FCM), web push ve WebView nejde
        if (jeAplikace()) return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

        const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);

        const subscribeToPush = async () => {
            try {
                const registration = await navigator.serviceWorker.ready;
                const existing = await registration.pushManager.getSubscription();
                if (existing) {
                    await sendSubscriptionToServer(existing);
                    return;
                }

                // Povolení už máme, jen zmizel odběr (upgrade iOS ho umí zahodit).
                // Přihlásit se znovu, a to BEZ ohledu na detekci instalace na ploše:
                // dřív se tahle větev schovávala za `navigator.standalone`, a když
                // ten po upgradu iOS vrátil false, funkce se tiše vrátila — žádný
                // odběr, žádný banner, žádná chyba. Notifikace prostě přestaly
                // chodit a nebylo podle čeho poznat proč. Když prohlížeč odběr
                // nedovolí, dozvíme se to z výjimky a nabídneme banner.
                if (Notification.permission === 'granted') {
                    try {
                        await doSubscribe(registration);
                    } catch (e) {
                        console.warn('[push] odběr se nepodařilo obnovit:', e);
                        setShowPushBanner(true);
                    }
                    return;
                }

                // Povolení chybí. Žádost o něj na iOS vyžaduje gesto uživatele
                // a mimo instalaci na ploše nemá smysl — tam nabídneme banner.
                if (isIos) {
                    if (!naPlose()) return;
                    if (Notification.permission === 'default') setShowPushBanner(true);
                    return;
                }
                // Android / desktop: požádat automaticky
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') return;
                await doSubscribe(registration);
            } catch (err) {
                console.warn('Push subscription failed:', err);
            }
        };

        const doSubscribe = async (registration) => {
            const vapidRes = await fetch('/api/push/vapid-public-key');
            const { key } = await vapidRes.json();
            if (!key) return;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(key),
            });
            await sendSubscriptionToServer(subscription);
        };

        // Uložit doSubscribe pro použití z banneru
        window._pushSubscribe = async () => {
            try {
                const registration = await navigator.serviceWorker.ready;
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') return;
                await doSubscribe(registration);
                setShowPushBanner(false);
            } catch (err) {
                console.warn('Push subscription failed:', err);
            }
        };

        subscribeToPush();

        const handleSwMessage = (event) => {
            if (event.data?.type === 'NOTIFICATION_CLICK') {
                if (event.data.modul === 'zpravy' && event.data.konverzace_id) {
                    navigateTo('zpravy', { konverzaceId: event.data.konverzace_id });
                } else if (event.data.notifikace_id) {
                    const cil = toastCilNotifikace({
                        id: event.data.notifikace_id,
                        modul: event.data.modul,
                        data: event.data.data || {},
                    }, user);
                    if (cil.notifikaceId) oznacitPrectenou(cil.notifikaceId);
                    navigateTo(cil.page, cil.params);
                } else {
                    navigateTo('aktivita');
                }
            }
        };
        navigator.serviceWorker.addEventListener('message', handleSwMessage);

        // Při návratu z pozadí (dlouhá neaktivita) re-ověřit subscription
        const handleVisibility = () => {
            if (!document.hidden) subscribeToPush();
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            navigator.serviceWorker.removeEventListener('message', handleSwMessage);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [user]);

    // Odznak na ikoně PWA = kolik toho čeká nepřečteného (zprávy + notifikace
    // + hořící úkoly) — totéž číslo, které počítá server do push notifikace.
    // Dřív se při každém otevření aplikace odznak smazal celý: zmizel, i když
    // nepřečtené zůstaly, a vrátil se až s další notifikací. Teď se drží podle
    // skutečného stavu a s přečtením klesá.
    const pocetNaIkone = (quickBadges.zpravy || 0) + (quickBadges.notifikace || 0);
    useEffect(() => {
        if (!user || !odznakyNacteny || !('setAppBadge' in navigator)) return;
        (pocetNaIkone > 0 ? navigator.setAppBadge(pocetNaIkone) : navigator.clearAppBadge()).catch?.(() => {});
    }, [user, odznakyNacteny, pocetNaIkone]);

    // Reconnect po probuzení telefonu / návratu z pozadí
    useEffect(() => {
        if (!user) return;
        let lastHidden = null;

        const handleVisibility = () => {
            if (document.hidden) {
                lastHidden = Date.now();
                return;
            }
            // Vrátili jsme se z pozadí
            const wasSuspended = lastHidden && (Date.now() - lastHidden > 30_000); // >30s v pozadí

            // Reconnect Echo WebSocket
            if (window.Echo?.connector?.pusher) {
                const state = window.Echo.connector.pusher.connection?.state;
                if (state !== 'connected') {
                    window.Echo.connector.pusher.connect();
                }
            }

            // Dispatch event pro komponenty — refetch dat
            window.dispatchEvent(new CustomEvent('app-resumed', { detail: { wasSuspended } }));

            // Refetch badge counts
            window.dispatchEvent(new Event('notifikace-changed'));
            window.dispatchEvent(new Event('chat-nova-zprava'));
        };

        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [user]);

    const handleLogin = (userData) => {
        setUser(userData);
        if (userData?.zamestnanec_id) {
            setCurrentPage('prehled');
            setCurrentParams({});
            window.history.replaceState({}, '', '/' + adresaZeStavu('prehled', {}));
        }
        if (userData?.zamestnanec_stav === 'novy') {
            setShowOnboarding(true);
        }
    };

    const handleLogout = async () => {
        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        // Odběr push patří odhlašovanému účtu — jinak by zařízení (sdílený tablet
        // na pobočce) dál dostávalo jeho notifikace. Mažeme jen na serveru, odběr
        // v prohlížeči zůstává a další přihlášení si ho převezme.
        try {
            const registrace = await navigator.serviceWorker?.getRegistration();
            const odber = await registrace?.pushManager?.getSubscription();
            if (odber) {
                await fetch('/api/push/unsubscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                    body: JSON.stringify({ endpoint: odber.endpoint }),
                });
            }
            navigator.clearAppBadge?.().catch?.(() => {});
        } catch { /* odhlášení nesmí zablokovat */ }
        await fetch('/auth/logout', { method: 'POST', headers: { 'X-CSRF-TOKEN': token } });
        setUser(null);
    };

    // Přepnutí stránky dřív překrylo obsah neprůhlednou plochou se spinnerem
    // na pevných 250 ms — i když byla data v paměti a stránka se vykreslila
    // hned. Z každého přepnutí modulu tak byl bílý záblesk. Novou stránku
    // teď jen prolneme (`.stranka-prichod`, 140 ms): co se načítá, si každá
    // stránka hlásí sama, a prázdná plocha na dvě desetiny vteřiny je míň
    // rušivá než clona přes všechno.

    // Přepnutí stavu bez zápisu do historie. Volá ho `navigateTo` (které
    // záznam přidá) i návrat tlačítkem Zpět (kde by nový záznam znamenal, že
    // se z aplikace nedá odejít).
    const aplikujStav = (page, params = {}) => {
        setCurrentPage(page);
        setCurrentParams(params || {});
        if (params.skoleniId !== undefined) setSelectedSkoleniId(params.skoleniId);
        if (params.konverzaceId !== undefined) setSelectedKonverzaceId(params.konverzaceId);
    };

    // Naposledy otevřené moduly — menu z nich dělá zkratku do toho, co člověk
    // dělá tenhle týden. Jde to stejným kanálem jako nedávné v hledání na
    // desktopu (`sidebar_state.search_recent`), jen se to plní i obyčejnou
    // navigací, ne pouze výběrem z palety. Zápis na server je odložený —
    // proklikat se pěti moduly nemá být pět requestů.
    const nedavneRef = useRef(null);
    const zapisNedavne = (page) => {
        const nalez = modulStranky(page);
        if (!nalez) return;
        // Modul s vlastním slotem v liště (Zprávy) mezi nedávné nepatří —
        // chodí se do něj nejčastěji a zabral by místo tomu, co se hledá.
        if (nalez.item.slotVListe) return;
        const id = nalez.item.id;
        setUser((u) => {
            const stare = u?.sidebar_state?.search_recent || [];
            if (stare[0] === id) return u;
            const nove = [id, ...stare.filter((r) => r !== id)].slice(0, 5);
            clearTimeout(nedavneRef.current);
            nedavneRef.current = setTimeout(() => {
                const token = document.querySelector('meta[name="csrf-token"]')?.content;
                fetch('/auth/sidebar-state', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token, Accept: 'application/json' },
                    body: JSON.stringify({ search_recent: nove }),
                }).catch(() => {});
            }, 800);
            return { ...u, sidebar_state: { ...(u?.sidebar_state || {}), search_recent: nove } };
        });
    };

    const navigateTo = (page, params = {}) => {
        // Společné Nastavení a samostatná Konfigurace zanikly (revize 2026-10-12):
        // co patří celé firmě, je v modulu Správa, zbytek v ⚙ modulů. Staré
        // odkazy (hledání, naposledy otevřené) vedou do Správy.
        if (page === 'nastaveni' || page === 'konfigurace') {
            page = 'sprava';
        }
        // Notifikace jsou na mobilu sekcí Přehledu, ne samostatnou stránkou.
        // Přesměrování je tady, aby všechny dosavadní prokliky (toast, push,
        // menu) vedly na jedno místo a nemusely o změně vědět. Na desktopu
        // zůstávají vlastní stránkou — tam je na ně v postranní liště zvonek.
        if (page === 'aktivita' && isMobile && user?.zamestnanec_id) {
            page = 'prehled';
            params = { ...params, tab: 'notifikace' };
        }
        const samePageNoParams = page === currentPage
            && !params.skoleniId && !params.konverzaceId
            && !params.zamestnanecId && !params.tab && !params.navratNa
            && !params.dodavatelId && !params.polozka && !params.id
            && !params.clanek && !params.qr && !params.zarizeni
            // Znalosti se procházejí parametry (druh, směrnice, recept) a Zpět
            // z detailu vede na stejnou stránku bez nich — nic se nemění, jen
            // když se nemění adresa.
            && (page !== 'znalosti-seznam'
                || adresaZeStavu(page, params) === adresaZeStavu(currentPage, currentParams));
        setMobileMenuOpen(false);
        if (samePageNoParams) return;
        // Adresa se mění spolu se stavem, takže Zpět vrací po krocích uvnitř
        // aplikace a teprve z prvního z nich ven.
        window.history.pushState({}, '', adresaZeStavu(page, params));
        zapisNedavne(page);
        aplikujStav(page, params);
    };

    // Zrušené moduly, které se staly záložkami jiného (Vzdělávání a Hodnocení
    // → Lidé, Rezervace a Zákazníci → Hosté). Staré stránky přesměruje jedno
    // místo, ať vedou kamkoli — odkaz, notifikace, pin, obnovení stránky
    // i Zpět. Záznam v historii se přepíše, ne přidá: jinak by Zpět skočilo
    // na starou adresu a znovu sem.
    useEffect(() => {
        const cil = PRESMEROVANE_STRANKY[currentPage]
            // Záložka Zákazníci odešla z Objednávek (Hostů) do modulu Zákazníci (2026-10-12)
            ?? (currentPage === 'objednavky' && currentParams?.tab === 'zakaznici' ? ['zakaznici', 'seznam'] : null);
        if (!cil) return;
        // Třetí prvek = převod staré záložky na novou (když cíl záložku neurčuje)
        const [modul, tab, zalozky] = cil;
        const novyTab = tab ?? (zalozky?.[currentParams?.tab] ?? currentParams?.tab);
        const params = { ...currentParams, ...(novyTab ? { tab: novyTab } : {}) };
        if (!novyTab) delete params.tab;
        window.history.replaceState({}, '', adresaZeStavu(modul, params));
        aplikujStav(modul, params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, currentParams?.tab]);

    // Modul Směrnice (`bezpecnost`) je od 2026-10-11 záložka Znalosti → Směrnice.
    // Kdo ji spravuje, přijde tam; ostatní na směrnice ve Znalostech ze spodní
    // lišty, kde je čtou a potvrzují.
    useEffect(() => {
        if (!user || currentPage !== 'bezpecnost') return;
        const [modul, params] = muze(user, 'znalosti.smernice')
            ? ['znalosti', { tab: 'smernice' }]
            : ['znalosti-seznam', { druh: 'smernice' }];
        window.history.replaceState({}, '', adresaZeStavu(modul, params));
        aplikujStav(modul, params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, user]);

    // Dokumentace (recepty, směrnice) bydlí od 2026-10-04 ve Znalostech. Staré
    // odkazy a notifikace (`prehled?tab=dokumentace&druh=…`) vedou sem.
    useEffect(() => {
        if (currentPage !== 'prehled' || currentParams?.tab !== 'dokumentace') return;
        const { tab, ...params } = currentParams;
        window.history.replaceState({}, '', adresaZeStavu('znalosti-seznam', params));
        aplikujStav('znalosti-seznam', params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, currentParams?.tab]);

    // Kolik receptů a směrnic čeká na potvrzení — odznak na slotu Znalosti
    const [kPotvrzeni, setKPotvrzeni] = useState(0);
    const nactiKPotvrzeni = useCallback(() => {
        fetch('/api/me/dokumentace', { headers: { Accept: 'application/json' } })
            .then(r => (r.ok ? r.json() : null))
            .then(d => setKPotvrzeni(d?.k_potvrzeni?.length || 0))
            .catch(() => {});
    }, []);
    useEffect(() => { if (user) nactiKPotvrzeni(); }, [user, nactiKPotvrzeni]);
    useEffect(() => {
        window.addEventListener('notifikace-changed', nactiKPotvrzeni);
        return () => window.removeEventListener('notifikace-changed', nactiKPotvrzeni);
    }, [nactiKPotvrzeni]);

    // Znalosti ze spodní lišty: jednoduchý seznam v sheetu, ne modul (ten je
    // pro administrátory). Otevírá ho slot lišty a odkudkoli událost
    // `znalosti-otevrit` (krok receptu) — `{ clanek }`, `{ qr }` nebo nic.
    const [znalostiSheet, setZnalostiSheet] = useState(null);
    useEffect(() => {
        const h = (e) => setZnalostiSheet(e.detail || {});
        window.addEventListener('znalosti-otevrit', h);
        return () => window.removeEventListener('znalosti-otevrit', h);
    }, []);
    // QR štítek a odkaz vedou na `#/znalosti?qr=…` / `?clanek=…`. Kdo modul
    // nemá (obsluha), dostane totéž v sheetu nad Přehledem — ne „bez přístupu".
    useEffect(() => {
        if (!user || currentPage !== 'znalosti' || smiNaStranku(user, 'znalosti')) return;
        const { qr, clanek } = currentParams || {};
        setZnalostiSheet(qr || clanek ? { qr, clanek } : {});
        window.history.replaceState({}, '', adresaZeStavu('prehled', {}));
        aplikujStav('prehled', {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, user]);

    // Zpět / Vpřed — stav se srovná podle adresy, historie se nedoplňuje.
    useEffect(() => {
        const naPopstate = () => {
            const cil = cestaZAdresy();
            if (cil) aplikujStav(cil.page, cil.params);
        };
        window.addEventListener('popstate', naPopstate);
        return () => window.removeEventListener('popstate', naPopstate);
    }, []);

    const renderPage = () => {
        // Modul bez přístupu (odkaz, notifikace, odebraný přístup) — ne rozbitá stránka
        if (user && (!smiNaStranku(user, currentPage) || !smiVAplikaci(currentPage))) {
            return <BezPristupu onNavigate={navigateTo} />;
        }
        // Účet bez zaměstnance nemá osobní Přehled — domovem je KPI Dashboard, když k němu má přístup
        const uvodBezZamestnance = () => (vidiModul(user, 'dashboard')
            ? <Dashboard user={user} />
            : <BezPristupu uvod onNavigate={navigateTo} />);
        switch (currentPage) {
            case 'prehled':         return user?.zamestnanec_id ? <Prehled user={user} onUserUpdate={handleUserUpdate} onNavigate={navigateTo} onLogout={handleLogout} initialTab={currentParams?.tab} navParams={currentParams} /> : uvodBezZamestnance();
            case 'dashboard':       return <Dashboard user={user} initialTab={currentParams?.tab} />;
            case 'zamestnanci':     return <Zamestnanci user={user} initialTab={currentParams?.tab} navParams={currentParams} />;
            case 'smeny':           return <Smeny user={user} initialTab={currentParams?.tab} />;
            // Přesměrují se na Lidé / Hosté efektem výš — do té doby nic nekreslit.
            case 'hodnoceni':
            case 'skoleni':
            case 'skoleni-editor':
            case 'rezervace':
            case 'zakaznicky-zazitek':
            case 'flotila':
            case 'flotila-karta-ridice': return null;
            case 'moje-skoleni-detail': return <MojeSkoleniDetail id={selectedSkoleniId} onNavigate={navigateTo} />;
            case 'report-smeny':       return <ReportSmenyStranka id={currentParams.id} onNavigate={navigateTo} />;
            case 'aktivita':           return <Aktivita onNavigate={navigateTo} user={user} />;
            case 'znalosti':           return <Znalosti user={user} initialTab={currentParams?.tab} navParams={currentParams} />;
            // Znalosti ze spodní lišty — seznam jako stránka (pro všechny),
            // `z` = obrazovka, odkud člověk přišel („Tady se hodí").
            case 'znalosti-seznam':    return <ZnalostiStranka user={user} navParams={currentParams} onNavigate={navigateTo}
                isDark={isDarkApp} isMobile={isMobile} onZmena={nactiKPotvrzeni} />;
            case 'zpravy':             return <Zpravy user={user} onNavigate={navigateTo} initialKonverzaceId={currentParams?.konverzaceId ?? selectedKonverzaceId} initialTab={currentParams?.tab} navParams={currentParams} />;
            // Suroviny a recepty jsou jeden modul (Receptář). Obě původní cesty se drží
            // naživu — vedou na ně prokliky z ingrediencí, z Financí i z Lami
            // a `initialPolozka.typ` stejně určí správnou záložku.
            case 'produkt-kvalita':
            case 'spizirna':           return <Spizirna user={user} onNavigate={navigateTo} initialPolozka={currentParams?.polozka} initialDodavatelId={currentParams?.dodavatelId} />;
            case 'balirna':            return <Balirna user={user} onNavigate={navigateTo} initialPolozka={currentParams?.polozka} initialDodavatelId={currentParams?.dodavatelId} />;
            // Objednávky a rezervace (2026-10-06 až 10-12 „Hosté“ i se zákazníky); Zákazníci od 10-12 zvlášť
            case 'objednavky':         return <Objednavky user={user} initialTab={currentParams?.tab} navParams={currentParams} onNavigate={navigateTo} />;
            case 'zakaznici':          return <Zakaznici user={user} initialTab={currentParams?.tab} navParams={currentParams} onNavigate={navigateTo} />;
            case 'checklisty':         return <Checklisty user={user} initialTab={currentParams?.tab} navParams={currentParams} />;
            case 'kamery':             return <Kamery user={user} />;
            // Energie jsou od 2026-10 měřidla v Kontrolách a měření — stará cesta (piny, notifikace) vede tam
            case 'energie':            return <Checklisty user={user} initialTab="parametry" navParams={currentParams} />;
            case 'udrzba':             return <Udrzba />;
            case 'rozvoz':             return <FlotilaErrorBoundary><Rozvoz user={user} initialTab={currentParams?.tab} params={currentParams} /></FlotilaErrorBoundary>;
            case 'sprava':             return <Sprava user={user} initialTab={currentParams?.tab} onNavigate={navigateTo} />;
            case 'podnety':            return <Podnety user={user} />;
            case 'finance':            return <Finance user={user} initialTab={currentParams?.tab} initialDokladId={currentParams?.dokladId} initialDodavatelId={currentParams?.dodavatelId} onNavigate={navigateTo} />;
            case 'doklady-mobil-upload': return <DokladyMobilUpload user={user} onNavigate={navigateTo} />;
            // Pokladna je nově tab Financí — stará route zůstává kvůli pinům,
            // odkazům a svalové paměti, jen otevře Finance rovnou na tom tabu.
            case 'pokladna':           return <Finance user={user} initialTab="pokladna" />;
            case 'media':              return <Media user={user} onUpload={handleMediaUpload} />;
            case 'social':             return <Social user={user} onNavigate={navigateTo} />;
            case 'web':                return <Web user={user} initialTab={currentParams?.tab} />;
            case 'kds-test':           return <KdsTestConsole />;
            case 'rozvoz-cockpit':     return <RozvozCockpit user={user} onNavigate={navigateTo} isDark={isDarkApp} />;
            default:                   return user?.zamestnanec_id ? <Prehled user={user} onUserUpdate={handleUserUpdate} onNavigate={navigateTo} onLogout={handleLogout} /> : uvodBezZamestnance();
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center"
                 style={{ background: 'var(--surface-base)' }}>
                <div style={{ color: 'var(--brand)' }} className="text-xl">🦙 Načítám...</div>
            </div>
        );
    }

    if (!user) {
        return <LoginScreen onLogin={handleLogin} />;
    }

    const sidebarWidth = isMobile ? '0px' : '60px';
    // Ladicí režim z adresy (`?ladit=rozvrzeni`) — čte se jednou, ne při každém
    // renderu, a v běžném provozu nic nepřidává.
    const nastaveniLadeni = new URLSearchParams(window.location.search).get('ladit');
    // Má uživatel co otevřít v menu modulů? Moduly s vlastním slotem v liště
    // (Zprávy) se do mřížky nekreslí, takže se ani nepočítají — bez téhle
    // podmínky by účet s jedinými Zprávami otevřel prázdné menu.
    // Aplikace je jen Cockpit — menu modulů v ní není.
    const maModulyVMenu = !jeAplikace() && viditelneSekce(user)
        .some(s => s.items.some(i => !i.slotVListe));
    // Součtový odznak zmizel s profilovkou v hlavičce. Odznaky teď nesou
    // jednotlivé sloty lišty — a tím konečně říkají, co vlastně čeká.
    // Stránky, co si na mobilu výšku řídí samy (scroll mají uvnitř) a jdou až
    // ke spodní hraně displeje — home indicator si ošetří samy rezervou pod
    // obsahem. Ostatní stránky scrollují v <main>.
    //   - Přehled zaměstnance: cockpit a taby se scrollem uvnitř
    //   - Receptář: třístupňový mobil podle normy (ModulLayout `mobilRezim`)
    const plnaVyskaMobil = isMobile && (
        (currentPage === 'prehled' && !!user?.zamestnanec_id)
        || currentPage === 'spizirna'
        // Zprávy jsou stejně připnutý modul jako receptář (`position: fixed`
        // od hlavičky ke spodní hraně) — rezervu pod lištou si drží uvnitř
        // scrollu seznamu, takže odsazení <main> by tu bylo jen navíc.
        || currentPage === 'zpravy'
        // Lidé: každá záložka je připnutá k výřezu a rezervu pod lištou si
        // drží uvnitř svého scrollu (seznam, karta, matice).
        || currentPage === 'zamestnanci'
        // Checklisty: seznamové záložky připnuté k výřezu jako Lidé
        || currentPage === 'checklisty'
        // Objednávky a Rezervace, Zákazníci (norma od 2026-10-01)
        || currentPage === 'objednavky'
        || currentPage === 'zakaznici'
        // Znalosti: připnuté jako Zprávy
        || currentPage === 'znalosti'
    );

    return (
        <SekceContext.Provider value={setSekceListy}>
        <div className="flex" style={{ height: '100%', overflow: 'hidden', color: 'var(--text-primary)' }}>
            {/* Brandová záře pod celým systémem — tři barvy identity, stejně jako
                v FofrLamaApp. Kreslí ji `.os-bg` (fixed, z-index -1), takže tenhle
                obal nesmí mít vlastní pozadí, jinak by ji překryl; základní plochu
                maluje <body> (`--surface-base`). Nahradila plochý `--page-gradient`. */}
            <div className="os-bg" aria-hidden="true" />

            <Sidebar
                currentPage={currentPage}
                onNavigate={navigateTo}
                user={user}
                onLogout={handleLogout}
                onUserUpdate={handleUserUpdate}
                isMobile={isMobile}
                pocetNotifikaci={quickBadges.notifikace}
            />

            {/* Nová verze — lišta s tlačítkem Aktualizovat (desktop i PWA) */}
            {/* Aplikace se aktualizuje sama — lišta webu „Aktualizovat" do ní nepatří */}
            {!jeAplikace() && <NovaVerze isMobile={isMobile} />}
            {/* Telefon jen na výšku — iOS orientaci z manifestu nezamkne */}
            <OtocNaVysku />

            {/* Mobilní hlavička — bez rámečku: ikona, název a profilovka leží přímo
                na pozadí, které nahoře splývá se stavovým řádkem a dolů plynule
                mizí. iOS kreslí nad aplikací na ploše stavový řádek plnou barvou
                pozadí stránky (průhledný být nemůže — iOS 27 přes něj maluje
                rozmazaný pás), takže mezi nimi není žádný šev. Obsah <main>
                odjíždí pod přechod a ztrácí se v něm. */}

            {/* Pruh pod stavovým řádkem — jen plná barva, žádný přechod přes
                obsah (dřív doznívající „výdech" kladl na horní okraj stránky
                šedý závoj). Obsah pod hodiny a baterii nevyjede; barva je
                `--horni-hrana`, kterou začíná i pozadí, takže pruh s ním
                splyne. Cockpit rozvozu má přes mapu vlastní horní ovládání. */}
            {isMobile && currentPage !== 'rozvoz-cockpit' && (
                <div aria-hidden="true" style={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 19,
                    height: 'var(--hlavicka-mobil-horni)',
                    pointerEvents: 'none',
                    background: 'var(--horni-hrana)',
                }} />
            )}

            <main
                className="flex-1 overflow-y-auto"
                style={{
                    marginLeft: sidebarWidth,
                    paddingTop: isMobile ? 'var(--hlavicka-mobil)' : '14px',
                    /* Mobil: spodní lišta není (navigace je v menu pod
                       profilovkou). Výplň = home indicator, ať konec stránky
                       nezajede pod něj; Přehled jde až k hraně (viz výše). */
                    /* Desktop: jen tolik, aby se karty nedotýkaly hrany okna.
                       Dřív 32 px dokola — na širokém monitoru z toho byl rám,
                       který kradl místo tabulkám. */
                    /* Mobil: místo pro plovoucí lištu. Stránky s vlastní výškou
                       (plnaVyskaMobil) si rezervu řeší uvnitř scrollu, ať jdou
                       obsahem až ke spodní hraně. */
                    paddingBottom: isMobile ? (plnaVyskaMobil ? 0 : REZERVA_LISTY) : '14px',
                    paddingLeft: isMobile ? '16px' : '16px',
                    paddingRight: isMobile ? '16px' : '16px',
                    transition: 'margin 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    minHeight: 0,
                    position: 'relative',
                }}
            >
                {/* Na připnutých stránkách ne. Ty leží `position: fixed` přes
                    celý viewport nad <main>, takže banner v normálním toku
                    zůstane pod nimi: prosvítá skrz průhledný spodek hlavičky
                    a nejde ani odklepnout. Místo pro něj tam žádné není —
                    rozvržení je do posledního pixelu rozpočtené na práci.
                    Nabídne se na kterékoliv jiné stránce. */}
                {!plnaVyskaMobil && !jeAplikace() && <PwaInstallBanner />}
                {/* `key` = stránka: každé přepnutí je nový prvek, takže se
                    animace prolnutí spustí znovu. */}
                <div key={currentPage} className="stranka-prichod" style={{ display: 'contents' }}>
                    {renderPage()}
                </div>
                {/* Ladicí překryv rozvržení — `?ladit=rozvrzeni`. Na telefonu je
                    jediný spolehlivý zdroj čísel o safe-area a výšce výřezu;
                    emulace v prohlížeči je má jinak. */}
                {nastaveniLadeni === 'rozvrzeni' && <LaditRozvrzeni />}
                {!isMobile && <MiniChat user={user} onNavigate={navigateTo} isDark={isDarkApp} currentPage={currentPage} />}
                <InAppToast onNavigate={navigateTo} isDark={isDarkApp} isMobile={isMobile} />
                {user && <PodnetWidget currentPage={currentPage} user={user} isMobile={isMobile} showFab={false} />}
                {/* Znalosti ze slotu lišty — seznam podle obrazovky, kde člověk je */}
                {user && znalostiSheet && (
                    <ZnalostiSheet
                        klic={[currentPage, sekceListy?.aktivni ?? currentParams?.tab].filter(Boolean).join('.')}
                        params={znalostiSheet} isDark={isDarkApp} isMobile={isMobile}
                        onClose={() => setZnalostiSheet(null)}
                        onUpravit={smiNaStranku(user, 'znalosti') ? (id) => { setZnalostiSheet(null); navigateTo('znalosti', { clanek: id }); } : null} />
                )}
                {uploadFiles.length > 0 && (
                    <UploadProgressPopup
                        uploadFiles={uploadFiles}
                        onClose={() => setUploadFiles([])}
                        onMinimize={() => setUploadPopupMin(m => !m)}
                        minimized={uploadPopupMin}
                        isDark={isDarkApp}
                    />
                )}
            </main>

            {/* iOS push notification banner — zobrazí se jen pokud nebylo uděleno povolení */}
            {showPushBanner && (
                <div style={{
                    position: 'fixed', bottom: isMobile ? 'calc(16px + env(safe-area-inset-bottom, 0px))' : '16px',
                    left: '50%', transform: 'translateX(-50%)',
                    zIndex: 60, borderRadius: 10, padding: '12px 16px',
                    background: isDarkApp ? 'rgba(25,25,40,0.97)' : 'white',
                    border: `1px solid ${isDarkApp ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                    display: 'flex', alignItems: 'center', gap: 12,
                    maxWidth: 'calc(100vw - 32px)', width: 340,
                }}>
                    <span style={{ fontSize: 24 }}>🔔</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Povolit oznámení</p>
                        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>Dostávejte zprávy i mimo aplikaci</p>
                    </div>
                    <button onClick={() => window._pushSubscribe?.()}
                        style={{ background: '#B47AEA', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        Povolit
                    </button>
                    <button onClick={() => setShowPushBanner(false)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, fontSize: 16 }}>✕</button>
                </div>
            )}

            {/* Plovoucí lišta u spodní hrany — hlavní navigace na mobilu.
                Leží pod celoobrazovkovými vrstvami (menu 70, chat 80, modály),
                takže ji soustředěná práce překryje. */}
            {/* V rozvozovém cockpitu lišta nesmí ležet přes tlačítka Zaplatit
                a Doručit — cockpit je při jízdě celoobrazovkový (návrh
                „Rozvoz cockpit — přebírání objednávek", rozhodnuto 2026-10-11). */}
            {isMobile && user && currentPage !== 'rozvoz-cockpit' && (
                <MobilniLista
                    currentPage={currentPage}
                    onNavigate={navigateTo}
                    onOpenMenu={() => setMobileMenuOpen(o => !o)}
                    menuOtevreno={mobileMenuOpen}
                    maModuly={maModulyVMenu}
                    onZnalosti={() => {
                        if (currentPage === 'znalosti-seznam') return;
                        const z = [currentPage, sekceListy?.aktivni ?? currentParams?.tab].filter(Boolean).join('.');
                        setZnalostiSheet(null);
                        navigateTo('znalosti-seznam', { z });
                    }}
                    znalostiOtevreno={currentPage === 'znalosti-seznam'}
                    modul={findPageMeta(currentPage)}
                    sekce={sekceListy}
                    /* Každý slot nese svůj počet a nic se nepočítá dvakrát.
                       Nepodepsané směrnice nese od 2026-10-11 jen slot
                       Znalosti (dřív i Notifikace přes odznak modulu Směrnice,
                       který zanikl). */
                    pocty={{
                        notifikace: quickBadges.notifikace,
                        zpravy: quickBadges.zpravy,
                        // Recepty a směrnice k potvrzení — potvrzují se ve Znalostech
                        znalosti: kPotvrzeni,
                    }}
                    isDark={isDarkApp}
                />
            )}

            {/* Celoobrazovkové menu pod profilovkou — jediná navigace na mobilu */}
            {isMobile && user && (
                <MobilniMenu
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    user={user}
                    currentPage={currentPage}
                    onNavigate={navigateTo}
                    pocty={{ zpravy: quickBadges.zpravy, aktivita: quickBadges.notifikace }}
                    isDark={isDarkApp}
                />
            )}
            {showOnboarding && (
                <OnboardingModal
                    user={user}
                    onComplete={() => {
                        setShowOnboarding(false);
                        fetch('/auth/me').then(r => r.ok ? r.json() : null).then(data => { if (data) setUser(data); });
                    }}
                    onLogout={async () => {
                        setShowOnboarding(false);
                        await handleLogout();
                    }}
                />
            )}
        </div>
        </SekceContext.Provider>
    );
}

oznacIosInstalaci();

createRoot(document.getElementById('app')).render(
    <ThemeProvider>
        <App />
        {/* Mimo <App />, takže odznak svítí i nad přihlašovací obrazovkou. */}
        <TestOdznak />
    </ThemeProvider>
);
